<template>
  <div
    id="app"
    class="h-100"
    :class="[skinClasses]"
  >
    <component :is="layout">
      <router-view />
    </component>

    <scroll-to-top v-if="enableScrollToTop" />

    <b-modal
      id="poe-session-timeout"
      v-model="sessionWarn"
      :title="$t('poe.sessionTitle')"
      :ok-title="$t('poe.sessionStay')"
      :cancel-title="$t('poe.sessionOut')"
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
      @ok.prevent="staySignedIn"
      @cancel="idleLogout"
    >
      {{ $t('poe.sessionBody', { time: sessionRemainLabel }) }}
    </b-modal>
  </div>
</template>

<script>
import ScrollToTop from '@core/components/scroll-to-top/ScrollToTop.vue'

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import { watch } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'

import { useWindowSize, useCssVar } from '@vueuse/core'

import store from '@/store'
import { initialAbility } from '@/libs/acl/config'
import {
  clearOfficerSession,
  isIdleExpired,
  isIdleWarning,
  isOfficerSession,
  remainingMs,
  requestIdleLogout,
  resetLogoutGuard,
  touchActivity,
} from '@/libs/sessionTimeout'

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

export default {
  components: {

    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,

    ScrollToTop,
  },
  data() {
    return {
      sessionWarn: false,
      sessionRemainMs: 0,
      sessionTimer: null,
    }
  },
  computed: {
    sessionRemainLabel() {
      const sec = Math.max(0, Math.ceil(this.sessionRemainMs / 1000))
      const min = Math.floor(sec / 60)
      const rem = sec % 60
      if (min > 0) {
        return `${min}m ${rem}s`
      }
      return `${sec}s`
    },
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
  },
  beforeCreate() {
    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
  setup() {
    const { skin, skinClasses } = useAppConfig()
    const { enableScrollToTop } = $themeConfig.layout

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    return {
      skinClasses,
      enableScrollToTop,
    }
  },
  mounted() {
    this.onActivity = () => touchActivity()
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click']
    events.forEach(name => window.addEventListener(name, this.onActivity, true))
    window.addEventListener('poe-idle-logout', this.idleLogout)
    window.addEventListener('storage', this.onStorage)
    this.$router.afterEach(() => {
      if (isOfficerSession()) {
        touchActivity()
      }
    })
    if (isOfficerSession()) {
      touchActivity()
    }
    this.sessionTimer = setInterval(this.checkIdle, 1000)
  },
  beforeDestroy() {
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click']
    events.forEach(name => window.removeEventListener(name, this.onActivity, true))
    window.removeEventListener('poe-idle-logout', this.idleLogout)
    window.removeEventListener('storage', this.onStorage)
    if (this.sessionTimer) {
      clearInterval(this.sessionTimer)
    }
  },
  methods: {
    onStorage(event) {
      if (event.key === 'userData' && !event.newValue && isOfficerSession() === false) {
        this.sessionWarn = false
      }
    },
    checkIdle() {
      if (!isOfficerSession()) {
        this.sessionWarn = false
        return
      }
      if (this.$route && this.$route.meta && this.$route.meta.redirectIfLoggedIn) {
        return
      }
      this.sessionRemainMs = remainingMs()
      if (isIdleExpired()) {
        requestIdleLogout()
        return
      }
      this.sessionWarn = isIdleWarning()
    },
    staySignedIn() {
      touchActivity(true)
      this.sessionWarn = false
    },
    idleLogout() {
      if (!isOfficerSession() && !localStorage.getItem('userData')) {
        resetLogoutGuard()
        this.sessionWarn = false
        return
      }
      this.sessionWarn = false
      this.$http.post('/api/auth/logout').catch(() => {})
      clearOfficerSession()
      resetLogoutGuard()
      this.$ability.update(initialAbility)
      if (!this.$route || this.$route.name !== 'auth-login') {
        this.$router.push({ name: 'auth-login' }).catch(() => {})
      }
    },
  },
}
</script>
